// Recurring personaSchedule events are wall-clock events: a participant who
// works "every Wednesday 09:30" must always show 09:30, in winter and summer
// alike. The stored rrule, however, carries an absolute-UTC DTSTART (e.g.
// 08:30Z), so an occurrence viewed across a DST boundary drifts by an hour.
// These helpers re-anchor the recurrence to a floating (zoneless) wall-clock
// time at read time, keeping the displayed clock time stable across DST.

const pad = (n) => String(n).padStart(2, '0')

const wallClockCompact = (d) =>
    `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`

const wallClockTime = (d) =>
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`

const floatingDtstartLine = (d) =>
    `DTSTART:${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${wallClockCompact(d)}`

// Rewrite an rrule string's DTSTART to a floating wall-clock time derived from
// the anchor's LOCAL clock, so a recurrence renders the same time across DST.
export const toWallClockRrule = (rruleString, anchorDate) => {
    if (!rruleString || !anchorDate) return rruleString
    const line = floatingDtstartLine(anchorDate)
    return /DTSTART:\S*/.test(rruleString)
        ? rruleString.replace(/DTSTART:\S*/, line)
        : `${line}\n${rruleString}`
}

// Stored exdates are absolute-UTC ("2026-06-03T08:30:00.000Z"). Re-anchor each
// to the floating wall-clock time so it still matches the floated occurrences.
export const toWallClockExdates = (exdates, anchorDate) => {
    if (!Array.isArray(exdates) || !anchorDate) return exdates
    const time = wallClockTime(anchorDate)
    return exdates.map((ex) => `${String(ex).split('T')[0]}T${time}`)
}

// A LOCAL Date as the floating "YYYY-MM-DDTHH:MM:SS" key FullCalendar's rrule
// plugin expects in `exdate` — the same shape toWallClockExdates produces, so a
// display-only exclusion (a schedule occurrence replaced by its segments) lines
// up with the stored ones.
export const toFloatingDateTime = (d) =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${wallClockTime(d)}`

// The rrule library yields "naive UTC" dates — wall-clock parts stored in UTC
// fields. Reinterpret those parts as LOCAL time to get a real local Date.
export const naiveUtcToLocal = (d) =>
    new Date(
        d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(),
        d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds()
    )

// Inverse: a LOCAL Date -> the naive-UTC instant rrule's between() expects.
export const localToNaiveUtc = (d) =>
    new Date(Date.UTC(
        d.getFullYear(), d.getMonth(), d.getDate(),
        d.getHours(), d.getMinutes(), d.getSeconds()
    ))

// End the series on a LOCAL day (inclusive): a floating UNTIL at 23:59:59, in
// the same naive wall-clock frame as the floated DTSTART. An earlier UNTIL the
// series already carries wins. Display only -- the stored rrule is untouched.
// Used for a participant's outflow date (updateverzoek Mijn Buuf, 2026-10-15).
export const withUntilDay = (rruleString, lastDay) => {
    if (!rruleString || !(lastDay instanceof Date)) return rruleString
    const until = `${lastDay.getFullYear()}${pad(lastDay.getMonth() + 1)}${pad(lastDay.getDate())}T235959`
    const existing = /UNTIL=(\d{8}T\d{6})Z?/.exec(rruleString)
    if (existing) {
        return existing[1] <= until ? rruleString : rruleString.replace(/UNTIL=[^;\s]*/, `UNTIL=${until}`)
    }
    return rruleString.replace(/(RRULE:[^\n]*)/, `$1;UNTIL=${until}`)
}

// Build the FullCalendar event props (rrule / exdate / exrule / duration) for a
// recurring task, with the recurrence floated to wall-clock time. Returns null
// for non-recurring tasks. `lastDay` (optional) ends the series on that day.
export const buildRecurringEventProps = (task, lastDay) => {
    if (!task?.rrule) return null
    const anchor = task.Timestamp?.toDate?.()
    if (!anchor) return null

    const hasExdate = Array.isArray(task.exdate) && task.exdate.length > 0

    return {
        rrule: withUntilDay(toWallClockRrule(task.rrule, anchor), lastDay),
        exdate: hasExdate ? toWallClockExdates(task.exdate, anchor) : undefined,
        exrule: task.exrule || undefined,
        duration:
            task.TimestampEnd && task.Timestamp
                ? task.TimestampEnd.toDate().getTime() - anchor.getTime()
                : null,
    }
}
